import React from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL_IMAGE } from "@/config/api.enpoints";
import type { UserData } from "@/types/user";
import useAuthStore from "@/store/useAuthStore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useDashboardTheme } from "@/pages/MeetingDashboard/themeHelper";
import { Pencil } from "lucide-react";

interface UserRow {
  name: string;
  email: string;
  isVerified: boolean;
  status?: "active" | "inactive";
  role: string;
  userImg?: string;
  id?: string;
}

interface TenantLicenseTableProps {
  data: UserRow[];
  showHeading?: boolean;
  onEditUser?: (user: UserData) => void;
}

const TenantLicenseTable: React.FC<TenantLicenseTableProps> = ({
  data,
  showHeading = true,
}) => {
  const { isDark, colors } = useDashboardTheme();
  const navigate = useNavigate();
  const { role } = useAuthStore();
  return (
    <div
      className="p-5 rounded-2xl overflow-x-auto shadow-sm transition-all duration-300"
      style={{
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        color: colors.text,
      }}
    >
      <h2 className="text-xl font-bold mb-4 flex justify-between items-center" style={{ color: colors.text }}>
        Recent Organization-wise User Table
        {showHeading && (
          <button
            className="text-xs font-semibold hover:underline cursor-pointer transition-colors"
            style={{ color: isDark ? "#60a5fa" : "#2563eb" }}
            onClick={() => navigate("/users")}
          >
            See all →
          </button>
        )}
      </h2>

      <table className="w-full text-sm text-left">
        <thead
          className="uppercase text-xs tracking-wider font-semibold"
          style={{
            background: colors.tableHeaderBg,
            color: colors.textMuted,
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <tr>
            <th className="py-3 px-6">Name</th>
            <th className="py-3 px-6">Email</th>
            <th className="py-3 px-6 text-center">Verified</th>
            <th className="py-3 px-6 text-center">Status</th>
            <th className="py-3 px-6">Role</th>
            <th className="py-3 px-6 text-center">
              {role === "MANAGER" ? "Views" : "Actions"}
            </th>
          </tr>
        </thead>

        <tbody>
          {data && data.length > 0 ? (
            data.map((user, idx) => (
              <tr
                key={idx}
                className="transition-colors hover:bg-black/5 dark:hover:bg-white/5"
                style={{
                  borderTop: `1px solid ${colors.border}`,
                  color: colors.text,
                }}
              >
                <td className="py-4 px-6">
                  <div className="flex items-center gap-4">
                    {user.userImg ? (
                      <img
                        src={
                          user.userImg.startsWith("http://") || user.userImg.startsWith("https://")
                            ? user.userImg
                            : API_BASE_URL_IMAGE + user.userImg
                        }
                        alt={user.name}
                        className="w-10 h-10 rounded-full object-cover border"
                        style={{ borderColor: colors.border }}
                      />
                    ) : (
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm"
                        style={{
                          backgroundColor: isDark ? "rgba(184, 248, 144, 0.15)" : "rgba(90, 157, 80, 0.15)",
                          color: isDark ? "#B8F890" : "#5a9d50",
                        }}
                      >
                        {user.name
                          ?.split(" ")
                          .map((p) => p[0])
                          .join("")
                          .toUpperCase()
                          .slice(0, 2)}
                      </div>
                    )}
                    <span className="font-semibold text-sm" style={{ color: colors.text }}>{user.name}</span>
                  </div>
                </td>

                <td className="py-4 px-6 text-sm" style={{ color: colors.textMuted }}>
                  {user.email}
                </td>

                <td className="py-4 px-6 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div
                      className="w-11 h-6 rounded-full relative transition duration-200 p-0.5"
                      style={{
                        backgroundColor: user.isVerified ? (isDark ? "#B8F890" : "#5a9d50") : (isDark ? "#4B5563" : "#CBD5E1"),
                      }}
                    >
                      <div
                        className={`w-5 h-5 bg-white rounded-full shadow-md transform transition duration-200 ${
                          user.isVerified ? "translate-x-5" : "translate-x-0"
                        }`}
                      />
                    </div>
                  </div>
                </td>

                <td className="py-4 px-6 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div
                      className="w-11 h-6 rounded-full relative transition duration-200 p-0.5"
                      style={{
                        backgroundColor: user.status === "active" ? (isDark ? "#B8F890" : "#5a9d50") : (isDark ? "#4B5563" : "#CBD5E1"),
                      }}
                    >
                      <div
                        className={`w-5 h-5 bg-white rounded-full shadow-md transform transition duration-200 ${
                          user.status === "active"
                            ? "translate-x-5"
                            : "translate-x-0"
                        }`}
                      />
                    </div>
                  </div>
                </td>

                <td className="py-4 px-6">
                  <span className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-md" style={{
                    background: isDark ? "rgba(90, 157, 80, 0.2)" : "rgba(90, 157, 80, 0.12)",
                    color: isDark ? "#86efac" : "#15803D",
                  }}>
                    {user.role}
                  </span>
                </td>

                <td className="py-4 px-6 text-center">
                  <button
                    onClick={() => navigate(`/user/${user.id}`)}
                    className="hover:scale-110 transition-transform p-1.5 rounded-lg cursor-pointer"
                    title={role === "MANAGER" ? "View" : "Edit"}
                  >
                    {role === "MANAGER" ? (
                      <VisibilityIcon
                        sx={{
                          fontSize: 20,
                          color: isDark ? "#90CAF9" : "#1D4ED8",
                        }}
                      />
                    ) : (
                      <Pencil size={18} style={{ color: isDark ? "#38bdf8" : "#0284c7" }} />
                    )}
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={6} className="py-8 text-center" style={{ color: colors.textMuted }}>
                No data available.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default TenantLicenseTable;
